// src/services/ClaudeService.ts
import { SlideContent } from '../components/SlideEditor';
import presentationService, { FileWithContent } from './PresentationService';
import { auth } from '../firebase';

export const CLAUDE_MAX_TOKENS = 200000; // Claude 3.7 Sonnet context window

export interface StreamingProgressCallback {
  onThinking?: (thinking: string) => void;
  onPartialResponse?: (text: string) => void;
  onComplete?: (html: string) => void;
  onError?: (error: string) => void;
}

class ClaudeService {
  private apiBaseUrl: string;

  constructor() {
    // Pages Functions API endpoints are relative to the domain root
    this.apiBaseUrl = '/api';
  }

  // Helper method to get auth token
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await auth.currentUser?.getIdToken();
    
    if (!token) {
      throw new Error('Not authenticated. Please log in again.');
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async countTokens(text: string, files: FileWithContent[] = []): Promise<number> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.apiBaseUrl}/count-tokens`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text, files })
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      return data.tokenCount;
    } catch (error) {
      console.error('Error counting tokens:', error);
      return Math.ceil((text.length / 4) + files.reduce((sum, file) => sum + Math.ceil(file.size / 200), 0));
    }
  }

  async generateSlides(
    content: SlideContent, 
    contextFiles: FileWithContent[] = [],
    progressCallback?: StreamingProgressCallback
  ): Promise<string> {
    // SECURITY: We only send essential data to the backend and keep prompt instructions server-side
    // This prevents exposing sensitive prompt engineering details in the frontend code
    const request = {
      title: content.title,
      content: content.content,
      contextFiles,
      format: {
        dimensions: {
          width: 1920,
          height: 1080
        },
        options: {
          pageBreaks: true,
          pdfOptimized: true
        }
      }
    };
    
    try {
      // Get authentication token for the POST request
      const headers = await this.getAuthHeaders();
      
      // Step 1: First make a POST request to submit data and get a clientId
      const postResponse = await fetch(`${this.apiBaseUrl}/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      });
      
      if (!postResponse.ok) {
        throw new Error(`API error: ${postResponse.status}`);
      }
      
      const { clientId } = await postResponse.json();
      
      if (!clientId) {
        throw new Error('No client ID received from server');
      }
      
      console.log('Received client ID:', clientId);
      
      // Get current auth token for the EventSource
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Step 2: Now create EventSource with the clientId parameter and auth token
      return new Promise((resolve, reject) => {
        const eventSource = new EventSource(
          `${this.apiBaseUrl}/generate?clientId=${clientId}&token=${encodeURIComponent(token)}`
        );
        
        // Track the HTML response as it comes in
        let currentThinking = '';
        let currentText = '';
        
        // Handle different event types
        eventSource.addEventListener('start', (event: Event) => {
          console.log('Streaming started');
        });
        
        eventSource.addEventListener('content_block_start', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          
          if (data.content_block?.type === 'thinking') {
            console.log('Thinking started');
            currentThinking = '';
          } else if (data.content_block?.type === 'text') {
            console.log('Response text started');
            currentText = '';
          }
        });
        
        eventSource.addEventListener('content_block_delta', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          
          if (data.delta?.type === 'thinking_delta') {
            currentThinking += data.delta.thinking;
            if (progressCallback?.onThinking) {
              progressCallback.onThinking(currentThinking);
            }
          } else if (data.delta?.type === 'text_delta') {
            currentText += data.delta.text;
            if (progressCallback?.onPartialResponse) {
              progressCallback.onPartialResponse(currentText);
            }
          }
        });
        
        eventSource.addEventListener('complete', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.log('Streaming completed');
          
          const finalHtml = data.html;
          
          eventSource.close();
          
          // No longer automatically save presentation to database
          // Let parent components handle saving as needed
          
          if (progressCallback?.onComplete) {
            progressCallback.onComplete(finalHtml);
          }
          
          resolve(finalHtml);
        });
        
        eventSource.addEventListener('error', (event: MessageEvent) => {
          console.error('EventSource error:', event);
          
          let errorMessage = 'Unknown error occurred';
          try {
            const data = JSON.parse(event.data);
            errorMessage = data.error || errorMessage;
          } catch (e) {
            // Use generic error message if parsing fails
          }
          
          if (progressCallback?.onError) {
            progressCallback.onError(errorMessage);
          }
          
          eventSource.close();
          reject(new Error(errorMessage));
        });
        
        eventSource.onerror = (error) => {
          console.error('EventSource connection error:', error);
          eventSource.close();
          reject(new Error('Connection error occurred'));
        };
      });
    } catch (error) {
      console.error('Error in generateSlides:', error);
      throw error;
    }
  }
}

const claudeService = new ClaudeService();
export default claudeService;